import type { ComponentChildren } from "preact";

export type ButtonProps = {
  children?: ComponentChildren;
  onClick?: (e: MouseEvent) => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  className?: string;
  variant?: "primary" | "secondary";
  arrow?: "none" | "left" | "right";
  ariaLabel?: string;
  /** When true, invert secondary button to white text/border (for dark/interstitial backgrounds). */
  onDark?: boolean;
};

export function Button({
  children = "Continue",
  onClick,
  type = "button",
  disabled,
  className = "",
  variant = "primary",
  arrow = "right",
  ariaLabel,
  onDark = false,
}: ButtonProps) {
  const isPrimary = variant === "primary";
  const widthClass = isPrimary
    ? "cq-min-w-[145px] md:cq-min-w-[176px]"
    : "cq-min-w-[105px] md:cq-min-w-[141px]";

  const base = [
    "cq-inline-flex cq-select-none cq-items-center cq-justify-center cq-h-[40px] md:cq-h-[48px] cq-cursor-pointer",
    `${widthClass} cq-rounded-full`,
    "cq-text-sm md:cq-text-base",
    "cq-transition-all",
    "focus:cq-outline-none disabled:cq-opacity-50 disabled:cq-cursor-not-allowed",
  ];

  const primary = [
    "cq-bg-cfa-blue-550 cq-text-white",
    "cq-shadow-sm",
    "hover:cq-bg-[#3676FF] active:cq-brightness-105",
    "focus-visible:cq-ring-2 focus-visible:cq-ring-cfa-blue-500 focus-visible:cq-ring-offset-2 cq-px-6 md:cq-px-10 cq-py-4 cq-tracking-[0.32px]",
  ];

  const secondary = onDark
    ? [
        "cq-bg-transparent !cq-border !cq-border-solid !cq-border-white cq-text-white",
        "hover:cq-bg-white/10",
        "focus-visible:cq-ring-2 focus-visible:cq-ring-white/70 focus-visible:cq-ring-offset-2 cq-px-6 md:cq-px-10 cq-py-4 cq-tracking-[0.32px]",
      ]
    : [
        "cq-bg-transparent !cq-border !cq-border-solid !cq-border-black cq-text-black",
        "hover:cq-bg-gray-600/5",
        "focus-visible:cq-ring-2 focus-visible:cq-ring-gray-700 focus-visible:cq-ring-offset-2 cq-px-6 md:cq-px-10 cq-py-4 cq-tracking-[0.32px]",
      ];

  const classes = [
    ...base,
    ...(isPrimary ? primary : secondary),
    className,
  ].join(" ");

  // Left arrow appears on secondary "Back" button; make it white on dark.
  const leftArrowFill = !isPrimary && onDark ? "#FFFFFF" : "#000000";

  return (
    <button
      type={type}
      onClick={onClick as any}
      disabled={disabled}
      aria-label={ariaLabel}
      className={classes}
    >
      {arrow === "left" && (
        <span aria-hidden className="cq-mr-3 cq-text-lg cq-leading-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M3.21659 6.66675H11.3333V5.33341H3.21659L6.94992 1.60008L5.99992 0.666748L0.666586 6.00008L5.99992 11.3334L6.94992 10.4001L3.21659 6.66675Z"
              fill={leftArrowFill}
            />
          </svg>
        </span>
      )}
      <span>{children}</span>
      {arrow === "right" && (
        <span aria-hidden className="cq-ml-3 cq-text-lg cq-leading-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M8.78317 6.66675H0.666504V5.33341H8.78317L5.04984 1.60008L5.99984 0.666748L11.3332 6.00008L5.99984 11.3334L5.04984 10.4001L8.78317 6.66675Z"
              fill="white"
            />
          </svg>
        </span>
      )}
    </button>
  );
}

export default Button;
